import { PlayerServiceAPI } from '@API/data-services/player-service'
import { DEFAULT_STALE_DATA_TIMEOUT } from '@AppState/app-config'
import { RootState } from '@AppState/redux-store.ts'
import { JsonView } from '@Components/common/json-view'
import { Pagination } from '@Components/common/pagination'
import { SideDrawer } from '@Components/common/side-drawer'
import { usePaginatedState } from '@Components/common/use-paginated-state'
import { LoadingRows } from '@Components/util/loading-rows'
import {
  PlayerRewardStatus,
  PlayerRewardWithOrigin,
  RewardOrigin,
} from '@Generated/back-office-client/src'
import { Badge, Box, Container, Flex, Heading, Skeleton, Table, Tabs, Text } from '@radix-ui/themes'
import { useQuery } from '@tanstack/react-query'
import { formatInTimeZone } from 'date-fns-tz'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

const PAGE_SIZE = 20

enum RewardTab {
  AWARDED = 'awarded',
  PENDING_FAILED = 'pending-failed',
}

const TAB_STATUSES: Record<RewardTab, PlayerRewardStatus[]> = {
  [RewardTab.AWARDED]: [PlayerRewardStatus.AWARDED],
  [RewardTab.PENDING_FAILED]: [PlayerRewardStatus.PENDING, PlayerRewardStatus.FAILED],
}

const formatOrigin = (origin: RewardOrigin): string => {
  switch (origin) {
    case RewardOrigin.SPIN_TO_WIN:
      return 'Spin to Win'
    case RewardOrigin.TICKET_SCAN:
      return 'Ticket Scan'
    case RewardOrigin.API:
      return 'API'
    default:
      return origin
  }
}

export const RewardHistory = () => {
  const params = useParams()
  const playerLookupId = params?.lookupId
  const [page, setPage] = usePaginatedState()
  const [tab, setTab] = useState<RewardTab>(RewardTab.AWARDED)
  const [showRewardDetails, setShowRewardDetails] = useState<PlayerRewardWithOrigin | undefined>(
    undefined,
  )

  const { activeCustomerMetadata } = useSelector((state: RootState) => state.users)
  const timezone = activeCustomerMetadata?.timezone ?? 'UTC'

  const { data: rewardsPage, isLoading } = useQuery({
    queryKey: [`${playerLookupId}-reward-history-${tab}-${page}`],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    queryFn: async () => {
      return PlayerServiceAPI.getPlayerRewards(
        playerLookupId!,
        page * PAGE_SIZE,
        (page + 1) * PAGE_SIZE,
        TAB_STATUSES[tab],
      )
    },
    enabled: !!playerLookupId,
  })

  const onTabChange = (value: string) => {
    setTab(value as RewardTab)
    setPage(0)
  }

  const isAwardedTab = tab === RewardTab.AWARDED

  const mapRewards = () => {
    return rewardsPage?.rewards.map((reward) => {
      return (
        <Table.Row key={reward.id}>
          <Table.RowHeaderCell>
            {formatInTimeZone(reward.createdAt, timezone, 'PP')}
            <br />
            {formatInTimeZone(reward.createdAt, timezone, 'p')}
          </Table.RowHeaderCell>
          <Table.Cell>{reward.rewardCategory}</Table.Cell>
          <Table.Cell>
            <a className="open-scan-details" onClick={() => setShowRewardDetails(reward)}>
              {reward.id}
            </a>
          </Table.Cell>
          {isAwardedTab ? (
            <Table.Cell>{formatOrigin(reward.origin)}</Table.Cell>
          ) : (
            <>
              <Table.Cell>
                <Badge color={reward.status === PlayerRewardStatus.FAILED ? 'red' : 'amber'}>
                  {reward.status}
                </Badge>
              </Table.Cell>
              <Table.Cell>
                {reward.latestAttemptMessage ? (
                  <Text color="red">{reward.latestAttemptMessage}</Text>
                ) : (
                  'N/A'
                )}
              </Table.Cell>
            </>
          )}
        </Table.Row>
      )
    })
  }

  return (
    <Container>
      <Flex direction="column" gap="3">
        <Tabs.Root value={tab} onValueChange={onTabChange}>
          <Tabs.List>
            <Tabs.Trigger value={RewardTab.AWARDED}>Awarded</Tabs.Trigger>
            <Tabs.Trigger value={RewardTab.PENDING_FAILED}>Pending / Failed</Tabs.Trigger>
          </Tabs.List>
        </Tabs.Root>

        <Skeleton loading={isLoading}>
          {rewardsPage?.rewards.length || isLoading ? (
            <Table.Root className="results-table">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>Date & Time</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Category</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Reward ID</Table.ColumnHeaderCell>
                  {isAwardedTab ? (
                    <Table.ColumnHeaderCell>Origin</Table.ColumnHeaderCell>
                  ) : (
                    <>
                      <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Error</Table.ColumnHeaderCell>
                    </>
                  )}
                </Table.Row>
              </Table.Header>

              <Table.Body>{isLoading ? <LoadingRows /> : mapRewards()}</Table.Body>
            </Table.Root>
          ) : (
            <Flex>No rewards for {playerLookupId}</Flex>
          )}
        </Skeleton>
        {rewardsPage?.totalResults ? (
          <Pagination
            totalItems={rewardsPage.totalResults}
            pageSize={PAGE_SIZE}
            currentPage={page}
            onPageChange={setPage}
          />
        ) : null}
      </Flex>
      {showRewardDetails ? (
        <SideDrawer onClose={() => setShowRewardDetails(undefined)}>
          <Flex direction="column" gap="5">
            <Flex direction="column" gap="2">
              <Heading as="h3" size="4">
                Reward Metadata
              </Heading>
              <Box className="scan-details-json">
                <JsonView data={showRewardDetails.rewardMeta ?? {}} />
              </Box>
            </Flex>
          </Flex>
        </SideDrawer>
      ) : null}
    </Container>
  )
}
